'use client'

import { useEffect, useRef } from 'react'

import { useIntl } from 'react-intl'

import { showSnackbar } from '@b-stock/fe-core/bstock-react'

import activeBidListingIdsQuery from '@/queries/activeBidListingIdsQuery'

import type { SearchAction } from './stateActions'

/**
 * Bridges the `myBids` toggle to `state.id`. There's no server-side "my
 * bids" filter and bid data isn't in the listings-search index, so toggling
 * `myBids` on fetches the buyer's active-bid live-listing IDs and applies
 * them via the existing `id` allowlist filter, which already composes with
 * search's other filters/sort/pagination.
 */
export const useMyBidsListingIdSync = ({
  isFilterEnabled,
  accountId,
  currentId,
  dispatch,
}: {
  isFilterEnabled: boolean
  accountId: string | null
  currentId: string[] | null
  dispatch: (action: SearchAction) => void
}): { isLoading: boolean; isError: boolean } => {
  const { formatMessage } = useIntl()
  // Fetched whenever a buyer is logged in, not gated on isFilterEnabled: the
  // query is a cheap, one-shot warm (staleTime: Infinity, no refetch
  // triggers) that's usually already cached by the time someone toggles the
  // filter on, collapsing the loading window to near-zero for that case.
  const { data, isError } = activeBidListingIdsQuery.useWithOpts(
    [accountId ?? ''],
    { enabled: !!accountId }
  )

  const lastIsFilterEnabled = useRef(false)
  const hasShownError = useRef(false)

  // `data === currentId` (reference equality — the dispatch below always
  // assigns `id: data` directly) is true exactly once the fetched ids have
  // been applied to state.id. Comparing against the applied filter, rather
  // than react-query's isFetching/data-presence flags, avoids a one-render
  // race where those flags already look "done" but the SET_STATE dispatch
  // (a separate effect, committed after this render) hasn't run yet.
  const idIsApplied = data !== undefined && data === currentId

  useEffect(() => {
    if (isFilterEnabled) {
      if (data && !idIsApplied) {
        dispatch({ type: 'SET_STATE', partialState: { id: data, page: 1 } })
      }
    } else if (lastIsFilterEnabled.current) {
      dispatch({ type: 'SET_STATE', partialState: { id: null, page: 1 } })
    }
    lastIsFilterEnabled.current = isFilterEnabled
  }, [isFilterEnabled, data, idIsApplied, dispatch])

  // Once retries are exhausted, surface the failure once per occurrence
  // instead of leaving the toggle stuck disabled/loading with no feedback.
  useEffect(() => {
    if (isFilterEnabled && isError && !hasShownError.current) {
      hasShownError.current = true
      showSnackbar({
        content: formatMessage({ id: 'Listings.myBidsFilter.error' }),
        options: {
          type: 'error',
          group: 'my-bids-filter',
          autoClose: true,
          closeButton: true,
        },
      })
    }
    if (!isError) {
      hasShownError.current = false
    }
  }, [isFilterEnabled, isError, formatMessage])

  return {
    isLoading: isFilterEnabled && !!accountId && !isError && !idIsApplied,
    isError,
  }
}
